import { createEmailHistoryApiInstance, getForwardedHost } from 'utils/emailHistory/apiUtils';

/**
 * Email History API Client - Bulk Emails
 * Base URL: /api3.2/emailHistory
 */

// Get the email history API instance
const emailHistoryApi = createEmailHistoryApiInstance('/api3.2/emailHistory');

/**
 * List all bulk emails (across all campaigns or filtered by campaignId)
 * Uses the general /bulk endpoint which supports optional campaignId filtering
 * @param {Object} params - Query parameters
 * @param {number} [params.campaignId] - Optional campaign ID filter
 * @param {number} [params.page=0] - Page number (0-indexed)
 * @param {number} [params.pageSize=25] - Items per page
 * @param {string} [params.sortBy='sendDate'] - Field to sort by
 * @param {string} [params.sortOrder='desc'] - Sort direction ('asc' or 'desc')
 * @param {string} [params.search] - Search by subject or campaign name (partial match)
 * @param {string} [params.dateFrom] - Filter by send date (ISO 8601)
 * @param {string} [params.dateTo] - Filter by send date (ISO 8601)
 * @returns {Promise<{rows: Array, totalRows: number, page: number, pageSize: number}>}
 */
export async function fetchAllBulkEmails(params = {}) {
  const {
    campaignId = null,
    page = 0,
    pageSize = 25,
    sortBy = 'sendDate',
    sortOrder = 'desc',
    search = null,
    dateFrom = null,
    dateTo = null,
  } = params;

  const queryParams = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sortBy,
    sortOrder,
  });

  if (campaignId) {
    queryParams.set('campaignId', String(campaignId));
  }

  if (search && search.trim()) {
    queryParams.set('search', search.trim());
  }

  if (dateFrom) {
    queryParams.set('dateFrom', dateFrom);
  }

  if (dateTo) {
    queryParams.set('dateTo', dateTo);
  }

  try {
    // Use general bulk endpoint: GET /api3.2/emailHistory/bulk
    const endpoint = `/bulk?${queryParams.toString()}`;

    const { data } = await emailHistoryApi.get(endpoint, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(emailHistoryApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch bulk emails');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching bulk emails:', error);
    throw error;
  }
}

/**
 * List all bulk emails within a specific campaign
 * Uses the campaign-specific endpoint: GET /api3.2/emailHistory/campaigns/:campaignId/emails
 * @param {Object} params - Query parameters
 * @param {number} params.campaignId - Campaign ID (required)
 * @param {number} [params.page=0] - Page number (0-indexed)
 * @param {number} [params.pageSize=25] - Items per page
 * @param {string} [params.sortBy='sendDate'] - Field to sort by
 * @param {string} [params.sortOrder='desc'] - Sort direction ('asc' or 'desc')
 * @param {string} [params.dateFrom] - Filter by send date (ISO 8601)
 * @param {string} [params.dateTo] - Filter by send date (ISO 8601)
 * @returns {Promise<{rows: Array, totalRows: number, page: number, pageSize: number}>}
 */
export async function fetchBulkEmails(params = {}) {
  const {
    campaignId,
    page = 0,
    pageSize = 25,
    sortBy = 'sendDate',
    sortOrder = 'desc',
    dateFrom = null,
    dateTo = null,
  } = params;

  // Campaign ID is required for this endpoint
  if (!campaignId) {
    throw new Error('campaignId is required to fetch bulk emails from a specific campaign');
  }

  const queryParams = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sortBy,
    sortOrder,
  });

  // Note: The campaign-specific endpoint doesn't support 'search' parameter
  // Only dateFrom and dateTo are supported

  if (dateFrom) {
    queryParams.set('dateFrom', dateFrom);
  }

  if (dateTo) {
    queryParams.set('dateTo', dateTo);
  }

  try {
    // Use campaign-specific endpoint as per API docs
    // GET /api3.2/emailHistory/campaigns/:campaignId/emails
    const endpoint = `/campaigns/${campaignId}/emails?${queryParams.toString()}`;

    const { data } = await emailHistoryApi.get(endpoint, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(emailHistoryApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch bulk emails');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching bulk emails:', error);
    throw error;
  }
}

/**
 * Get comprehensive statistics for a specific bulk email
 * @param {number} emailId - The bulk email ID
 * @returns {Promise<Object>} Bulk email details
 */
export async function fetchBulkEmailDetails(emailId) {
  try {
    const { data } = await emailHistoryApi.get(`/bulk/${emailId}`, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(emailHistoryApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch bulk email details');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching bulk email details:', error);
    throw error;
  }
}

/**
 * Get a paginated list of all recipients for a specific bulk email
 * @param {number} emailId - The bulk email ID
 * @param {Object} params - Query parameters
 * @returns {Promise<{rows: Array, totalRows: number, page: number, pageSize: number}>}
 */
export async function fetchBulkEmailRecipients(emailId, params = {}) {
  const {
    page = 0,
    pageSize = 50,
    sortBy = 'emailAddress',
    sortOrder = 'asc',
    status = null,
    search = '',
  } = params;

  const queryParams = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
    sortBy,
    sortOrder,
  });

  if (status) {
    queryParams.set('status', status);
  }

  if (search) {
    queryParams.set('search', search);
  }

  try {
    const { data } = await emailHistoryApi.get(`/bulk/${emailId}/recipients?${queryParams.toString()}`, {
      headers: {
        'Content-Type': 'application/json;charset=UTF-8',
        'x-forwarded-host': getForwardedHost(emailHistoryApi.defaults.baseURL),
      },
    });

    if (!data.success) {
      throw new Error(data.error?.message || 'Failed to fetch bulk email recipients');
    }

    return data.data;
  } catch (error) {
    console.error('Error fetching bulk email recipients:', error);
    throw error;
  }
}

